import * as logClient from "@classdojo/log-client";
import { ProductEvent } from "@classdojo/log-client/lib/client/client";
import { PodInstallFunction } from "@web-monorepo/shared/podInfra";
import identity from "lodash/identity";
import { navigate } from "src/utils/reactRouterNavigate";
import type { AnyAction } from "redux";
import { SagaIterator, Task } from "redux-saga";
import { call, put, take, fork, join, takeEvery } from "redux-saga/effects";
import Cookies from "universal-cookie";
import * as Android from "./androidCommands";
import * as Ios from "./iosCommands";
import { mountPoint } from "src/pods/realtime/util/role";
import { userAgentPlatform } from "src/utils/userAgentPlatform";

const ANDROID_WEBVIEW_INIT_ACTION = "android/initWebview";
const IOS_WEBVIEW_INIT_ACTION = "ios/initWebview";
const SET_REMOTE_PLATFORM = "webview/setRemotePlatform";
const NAVIGATE_ACTION = "navigate";

const STATE_KEY = "webview";

type WebViewState = {
  platform: "ios" | "android" | "web" | null;
  buildNumber: number | null;
};

let platform = typeof window === "undefined" ? null : userAgentPlatform();

const initialState: WebViewState = {
  platform: null, // can be "ios", "android", "web"
  buildNumber: null, // can be null for web and an Int build number for mobile
};
const reducer = (state: WebViewState = initialState, action: AnyAction) => {
  if (action.type === SET_REMOTE_PLATFORM) {
    return {
      ...state,
      platform: action.payload.platform,
      buildNumber: action.payload.buildNumber,
    };
  }
  return state;
};

export function* initSaga(): SagaIterator {
  if (
    // parent toolkit
    window.location.pathname.includes("beyondToolkit") ||
    // nps survey
    window.location.pathname.includes("npsSurvey") ||
    // teacher toolkit on mobile webview
    mountPoint === "mobile"
  ) {
    // attempt to read hash value if we don't have a session cookie already
    const cookies = new Cookies();
    if (!cookies.get("dojo_login.sid")) {
      const session = window.location.hash.replace(/^#/, ""); // remove the # at the very front
      if (session.trim().length > 0) {
        const domain = window.location.hostname.endsWith(".com") ? ".classdojo.com" : ".classdojo.test";
        cookies.set("dojo_login.sid", session, {
          domain,
          path: "/",
          expires: new Date("2031-12-31"),
          encode: identity, // otherwise the default behavior will URLEncode (and we have % in the cookie)
        });
      }
    }

    // set up mobile communication channels
    let isIos;
    const awaitInit: Task = yield fork(function* () {
      const action: AnyAction = yield take([ANDROID_WEBVIEW_INIT_ACTION, IOS_WEBVIEW_INIT_ACTION]);
      isIos = action.type === IOS_WEBVIEW_INIT_ACTION;
      platform = action.type === IOS_WEBVIEW_INIT_ACTION ? "ios" : "android";
      yield put({
        type: SET_REMOTE_PLATFORM,
        payload: {
          platform,
          buildNumber: action.type === IOS_WEBVIEW_INIT_ACTION ? action.meta.buildNumber : action.buildNumber,
        },
      });
    });
    const awaitInteractable: Task = yield fork(function* () {
      yield take("internal/interactable");
    });
    yield join([awaitInit, awaitInteractable]);
    yield call(isIos ? Ios.init : Android.init);
  } else {
    // teacher toolkit mounted in teach web
    platform = "web";
    yield put({
      type: SET_REMOTE_PLATFORM,
      payload: {
        platform: "web",
      },
    });
  }
}

export function* navigateSaga() {
  yield takeEvery(NAVIGATE_ACTION, function* ({ payload: route }: AnyAction) {
    try {
      yield call(navigate, route);
    } catch (ex: any) {
      // Augmenting the error with more context to determine if this is the source of
      // next.js "Invariant: attempted to hard navigate to the same URL" errors
      throw new Error(`navigateSaga: ${ex.message}`, { cause: ex });
    }
  });
}

export function showNativeNav(shouldShow: boolean) {
  if (platform === "ios") {
    Ios.showNativeNav(shouldShow);
  } else if (platform === "android") {
    Android.showNativeNav(shouldShow);
  }
}

export function closeWebview() {
  if (platform === "ios") {
    Ios.closeWebview();
  } else if (platform === "android") {
    Android.closeWebview();
  }
}

export function openSalesPage() {
  if (platform === "ios") {
    Ios.openSalesPage();
  } else if (platform === "android") {
    Android.openSalesPage();
  }
}

export function openPointsSheet() {
  if (platform === "ios") {
    Ios.openPointsSheet();
  } else if (platform === "android") {
    Android.openPointsSheet();
  }
}

export function logEvent(event: ProductEvent, role: string | null = null) {
  //Platform prefix is added during ios and android native event logging
  if (platform === "ios") {
    //Role prefix is NOT added during iOS native event logging, so add it here
    if (role) event.eventName = `${role}.${event.eventName}`;
    Ios.logEvent(event);
  } else if (platform === "android") {
    //Role prefix is added during android native event logging, so skip it here to avoid "parent.parent" issue
    Android.logEvent(event);
  } else {
    //Need to add web platform prefix and role here
    if (role) event.eventName = `${role}.${event.eventName}`;
    return logClient.logEvent({ ...event, eventName: `web.${event.eventName}` });
  }
}

const install: PodInstallFunction = (installReducer, installSaga) => {
  installSaga(initSaga);
  installSaga(navigateSaga);
  installReducer(STATE_KEY, reducer);
};

export default install;
